import {type LoginResponse, type ReloadSessionResponse} from '@perfectpost/perfect-post-common';
import {compareVersions} from 'compare-versions';

import {type ExtensionTransport} from './transport';
import {VITE_EXTENSION_MIN_VERSION} from '../../config';

// État de connexion à l'extension, sorti de Redux. Cœur pur : la seule dépendance à
// l'extension passe par le transport injecté, ce qui rend la connexion testable sans extension.

export type ConnectionStatus = 'not_started' | 'searching' | 'found' | 'not_found';

export type AccountStatus = 'ok' | 'mismatch' | 'missing_association' | 'no_session';

export type ExtensionConnectionSnapshot = {
  status: ConnectionStatus;
  isConnected: boolean;
  version?: string;
  isOutdated: boolean;
  linkedinSessionAvailable: boolean;
  csrfSearchPending: boolean;
  accountMismatch: boolean;
  missingAccountAssociation: boolean;
  accountStatus?: AccountStatus;
  // `entityUrn` compris : c'est le champ sur lequel l'extension décide le conflit, donc le seul
  // qui distingue les deux profils quand le `vanityName` est identique des deux côtés.
  accountMismatchDetails?: {
    sessionVanity?: string;
    sessionEntityUrn?: string;
    currentVanity?: string;
    currentEntityUrn?: string;
  };
  vanityName?: string;
  perfectPostId?: string;
  perfectPostEmail?: string;
};

export type ExtensionConnection = {
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => ExtensionConnectionSnapshot;
  // Vrai tant que l'extension n'a pas été déclarée introuvable (équivaut à !isExtensionNotFound).
  isAvailable: () => boolean;
  // `silent` (revalidation de fond) : ne repasse pas par l'état `searching`, pour ne pas
  // réinitialiser l'UI qui en dépend (checklist onboarding, bloc de remédiation, popup).
  checkInstall: (options?: {silent?: boolean}) => Promise<void>;
  reloadSession: () => Promise<ReloadSessionResponse | undefined>;
  login: (credentials: {email: string; password: string}) => Promise<LoginResponse | undefined>;
  // Demande à l'extension d'ouvrir sa popup d'action (reconnexion session LinkedIn). `opened=false`
  // quand le navigateur refuse (pas de user-gesture), ne supporte pas l'API, ou en cas d'erreur.
  openPopup: () => Promise<{opened: boolean; reason?: string}>;
};

const INITIAL_SNAPSHOT: ExtensionConnectionSnapshot = {
  status: 'not_started',
  isConnected: false,
  isOutdated: false,
  linkedinSessionAvailable: false,
  csrfSearchPending: false,
  accountMismatch: false,
  missingAccountAssociation: false,
};

const isOutdatedVersion = (version: string): boolean =>
  VITE_EXTENSION_MIN_VERSION !== undefined && compareVersions(version, VITE_EXTENSION_MIN_VERSION) < 0;

export function createExtensionConnection(transport: ExtensionTransport): ExtensionConnection {
  let snapshot: ExtensionConnectionSnapshot = INITIAL_SNAPSHOT;
  const listeners = new Set<() => void>();

  const setSnapshot = (next: ExtensionConnectionSnapshot) => {
    snapshot = next;
    listeners.forEach((listener) => listener());
  };

  const checkInstall = async (options?: {silent?: boolean}) => {
    if (!options?.silent) {
      setSnapshot({...snapshot, status: 'searching'});
    }
    try {
      const response = await transport.request({source: 'perfectpost', type: 'check_install'});
      const payload = response?.payload as (typeof response.payload & {csrfSearchPending?: boolean}) | undefined;
      if (payload === undefined) {
        setSnapshot({...INITIAL_SNAPSHOT, status: 'not_found'});
        return;
      }
      setSnapshot({
        status: 'found',
        isConnected: payload.isAuthenticated,
        version: payload.extensionVersion,
        isOutdated: isOutdatedVersion(payload.extensionVersion),
        linkedinSessionAvailable: payload.csrfTokenExists ?? false,
        csrfSearchPending: payload.csrfSearchPending ?? false,
        accountMismatch: payload.accountMismatch ?? false,
        missingAccountAssociation: payload.missingAccountAssociation ?? false,
        accountStatus: payload.accountStatus,
        accountMismatchDetails: payload.accountMismatchDetails
          ? {
              sessionVanity: payload.accountMismatchDetails.sessionVanity,
              sessionEntityUrn: payload.accountMismatchDetails.sessionEntityUrn,
              currentVanity: payload.accountMismatchDetails.currentVanity,
              currentEntityUrn: payload.accountMismatchDetails.currentEntityUrn,
            }
          : undefined,
        vanityName: payload.linkedInVanity,
        perfectPostId: payload.perfectPostId,
        perfectPostEmail: payload.perfectPostEmail,
      });
    } catch {
      setSnapshot({...INITIAL_SNAPSHOT, status: 'not_found'});
    }
  };

  const reloadSession = async () => {
    try {
      return await transport.request({source: 'perfectpost', type: 'reload_session'});
    } catch {
      return undefined;
    }
  };

  const login = async (credentials: {email: string; password: string}) => {
    try {
      const response = await transport.request({source: 'perfectpost', type: 'login_only', payload: credentials});
      const payload = response.payload;
      const isConnected =
        'authenticated' in payload
          ? true
          : 'challengeName' in payload && payload.challengeName === 'NEW_PASSWORD_REQUIRED'
            ? false
            : false;
      setSnapshot({...snapshot, isConnected});
      return response;
    } catch {
      setSnapshot({...snapshot, isConnected: false});
      return undefined;
    }
  };

  const openPopup = async (): Promise<{opened: boolean; reason?: string}> => {
    try {
      const response = await transport.request({source: 'perfectpost', type: 'open_popup'});
      return response.payload;
    } catch {
      return {opened: false, reason: 'error'};
    }
  };

  return {
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot: () => snapshot,
    isAvailable: () => snapshot.status !== 'not_found',
    checkInstall,
    reloadSession,
    login,
    openPopup,
  };
}
